import { Button, Column, FlexGrid as Grid, Row } from '@carbon/react';
import { memo, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import style from './well-analysis.module.scss';
import { Rotate as ResetIcon } from '@carbon/react/icons';
import { omit } from 'lodash-es';
import { useProductionVersions, useRecordVersions } from './hooks';
import { VersionsBySource, WellVersions } from './types';
import { DcaScenariosResponse } from 'types/api-responses';
import { VersionDropdown } from './version-dropdown';
import { DcaScenarioFilter } from './dca-scenario-filter';
import { WellAnalysisContent } from './well-analysis-content';
import {
  isDailyProductionSource,
  ProductionSourceMultiSelect,
} from 'components/production-source-dropdown';
import {
  combineSourceVersions,
  splitSourceVersions,
  withCurrentVersions,
} from './production-version-utils';
import type { ProductionDataSource } from 'types/api-responses';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  refetchInsightForecastVersionsTriggerAtom,
  totalInsightForecastVersionsAtom,
} from './atoms';

const insightForecastVersionHref =
  '/queries/reservoir/bulk_well_dca_forecasts/versions';

/**
 * The Well Analysis section: the production sources and versions to show, the
 * forecast versions beside them, and the chart and figures they feed.
 *
 * @param {object} props - The well, where its production and forecasts live, and the sources it has.
 * @return {JSX.Element} The version selectors above the section body.
 */
function WellAnalysisImpl({
  forecastVersionHref,
  recordHref,
  recordVersionHref,
  dailyRecordHref,
  dailyRecordVersionHref,
  productionDataSources,
  uwi,
  wellName,
}: {
  uwi: string;
  wellName?: string;
  recordHref: string;
  recordVersionHref: string;
  /** Daily production and its versions; absent where the well response doesn't link them. */
  dailyRecordHref?: string;
  dailyRecordVersionHref?: string;
  /** The sources the well has production for. Absent means IHS only. */
  productionDataSources?: ProductionDataSource[];
  forecastHref: string;
  forecastVersionHref: string;
}) {
  const availableSources = useMemo<ProductionDataSource[]>(() => {
    if (!productionDataSources) return ['ihs'];
    if (dailyRecordHref && dailyRecordVersionHref) return productionDataSources;
    return productionDataSources.filter((s) => !isDailyProductionSource(s));
  }, [productionDataSources, dailyRecordHref, dailyRecordVersionHref]);

  // Daily production is shown by default when the well has it. Sources picked
  // in the multi-select hold only for the well they were picked on, and only
  // while that well still has them. The first one is the chart's own
  // production line, which the figures and the DCA Forecast table follow.
  const [pickedSources, setPickedSources] = useState<{
    uwi: string;
    sources: ProductionDataSource[];
  } | null>(null);
  const productionSources = useMemo<ProductionDataSource[]>(() => {
    const picked =
      pickedSources?.uwi === uwi
        ? pickedSources.sources.filter((s) => availableSources.includes(s))
        : [];
    if (picked.length > 0) return picked;
    return [availableSources.find(isDailyProductionSource) ?? 'ihs'];
  }, [pickedSources, uwi, availableSources]);

  const ihsVersions = useRecordVersions(recordVersionHref, uwi);
  const dailyVersions = useRecordVersions(
    dailyRecordVersionHref ?? recordVersionHref,
    uwi
  );
  const versionsBySource = { ihs: ihsVersions, scada: dailyVersions };
  const shownVersions = productionSources.map(
    (source) => versionsBySource[source]
  );
  const ihsRecordVersions = ihsVersions.recordVersions;
  const dailyRecordVersions = dailyVersions.recordVersions;
  const recordVersionsBySource = useMemo<VersionsBySource>(
    () => ({ ihs: ihsRecordVersions, scada: dailyRecordVersions }),
    [ihsRecordVersions, dailyRecordVersions]
  );

  // The versions picked for each source. A source with nothing picked shows
  // its current version. Like the picked sources, they hold only for the well
  // they were picked on.
  const [pickedRecordVersions, setPickedRecordVersions] = useState<{
    uwi: string;
    versions: VersionsBySource;
  } | null>(null);
  const pickedForWell = useMemo<VersionsBySource>(
    () =>
      pickedRecordVersions?.uwi === uwi ? pickedRecordVersions.versions : {},
    [pickedRecordVersions, uwi]
  );
  const selectedRecordVersions = useMemo(
    () =>
      withCurrentVersions(
        productionSources,
        pickedForWell,
        recordVersionsBySource
      ),
    [productionSources, pickedForWell, recordVersionsBySource]
  );
  // One list of every shown source's versions, labelled by source.
  const combinedRecordVersions = useMemo(
    () => combineSourceVersions(productionSources, recordVersionsBySource),
    [productionSources, recordVersionsBySource]
  );
  const combinedSelectedVersions = useMemo(
    () => combineSourceVersions(productionSources, selectedRecordVersions),
    [productionSources, selectedRecordVersions]
  );
  const [selectedForecastVersions, setSelectedForecastVersions] =
    useState<WellVersions>([]);
  const [selectedInsightForecastVersions, setSelectedInsightForecastVersions] =
    useState<WellVersions>([]);
  const [resetKey, setResetKey] = useState(0);
  // The scenario the Insights forecast versions beside it are drawn from.
  const [dcaScenario, setDcaScenario] = useState<DcaScenariosResponse | null>(
    null
  );
  const refetchTrigger = useAtomValue(
    refetchInsightForecastVersionsTriggerAtom
  );
  const setTotalInsightForecastVersions = useSetAtom(
    totalInsightForecastVersionsAtom
  );
  const {
    forecastVersions,
    setForecastVersionResponse,
    isForecastVersionsLoading,
    forecastVersionsError,
    insightForecastVersions,
    setInsightForecastVersionResponse,
    isInsightForecastVersionsLoading,
    insightForecastVersionsError,
  } = useProductionVersions(
    recordVersionHref,
    forecastVersionHref,
    insightForecastVersionHref,
    uwi
  );

  // Refetch insight forecast versions when DCA forecast is saved
  useEffect(() => {
    if (refetchTrigger > 0) {
      setInsightForecastVersionResponse(() => ({
        query: {
          wells: [{ uwi, dcaScenarioId: dcaScenario?.id }],
        },
      }));
    }
  }, [refetchTrigger, uwi, dcaScenario, setInsightForecastVersionResponse]);

  useEffect(() => {
    setTotalInsightForecastVersions(insightForecastVersions.length);
  }, [insightForecastVersions, setTotalInsightForecastVersions]);

  // The scenario the current version list was fetched for. It starts as null,
  // which matches the unfiltered fetch the versions atom does on mount, so the
  // first run does nothing and later runs fetch once per scenario change.
  const fetchedForScenarioRef = useRef<number | null>(null);

  // How far along the list for a newly chosen scenario is: 'requested' once it
  // is asked for, and 'loading' once the request is seen running. The effect
  // below selects the latest forecast when the list arrives after that.
  const selectLatestRef = useRef<'idle' | 'requested' | 'loading'>('idle');

  // A forecast belongs to one scenario, so changing the scenario clears the
  // selected version and fetches the list again.
  useEffect(() => {
    const scenarioId = dcaScenario?.id ?? null;
    if (fetchedForScenarioRef.current === scenarioId) return;
    fetchedForScenarioRef.current = scenarioId;
    selectLatestRef.current = 'requested';

    setSelectedInsightForecastVersions([]);
    setInsightForecastVersionResponse(() => ({
      query: {
        wells: [{ uwi, ...(scenarioId ? { dcaScenarioId: scenarioId } : {}) }],
      },
    }));
  }, [dcaScenario, uwi, setInsightForecastVersionResponse]);

  // The versions come back newest first. VersionDropdown only fills an empty
  // selection when the list length changes, so two scenarios with the same
  // number of forecasts would leave nothing selected.
  //
  // The previous scenario's list is still in place on the render that asks for
  // the new one, and stays there while it loads. Selecting from it would pick
  // a forecast from the wrong scenario, so this waits for the request to be
  // seen running and then to finish.
  useEffect(() => {
    if (selectLatestRef.current === 'requested') {
      if (isInsightForecastVersionsLoading) selectLatestRef.current = 'loading';
      return;
    }
    if (
      selectLatestRef.current !== 'loading' ||
      isInsightForecastVersionsLoading
    ) {
      return;
    }
    selectLatestRef.current = 'idle';

    if (insightForecastVersionsError || insightForecastVersions.length === 0) {
      return;
    }
    setSelectedInsightForecastVersions([insightForecastVersions[0]]);
  }, [
    insightForecastVersions,
    isInsightForecastVersionsLoading,
    insightForecastVersionsError,
  ]);

  const isRecordVersionsLoading = shownVersions.some(
    (versions) => versions.isRecordVersionsLoading
  );
  // The dropdown shows an error when any shown source's versions failed, since
  // that source's versions are missing from it. The page-level Refetch button
  // appears only when every request failed.
  const isAnyRecordVersionsError = shownVersions.some((versions) =>
    Boolean(versions.recordVersionsError)
  );
  const isRecordVersionsError = shownVersions.every((versions) =>
    Boolean(versions.recordVersionsError)
  );
  const isForecastVersionsError = Boolean(forecastVersionsError);
  const isInsightForecastVersionsError = Boolean(insightForecastVersionsError);
  const isLoading = isRecordVersionsLoading || isForecastVersionsLoading;
  /**
   * Asks again for the versions of each shown source whose versions failed.
   * A source that loaded is left alone, so its versions stay in the dropdown.
   */
  const refetchRecordVersions = () =>
    shownVersions
      .filter((versions) => versions.recordVersionsError)
      .forEach((versions) =>
        versions.setRecordVersionResponse(() => ({ uwis: [uwi] }))
      );

  // Clearing the picked production versions leaves each source on its current
  // version.
  const handleReset = useCallback(() => {
    const currentForecastVersion = forecastVersions.find(
      (version) => version.versionId === 'current'
    );
    const currentInsightForecastVersion = insightForecastVersions[0];
    if (currentForecastVersion) {
      setSelectedForecastVersions([currentForecastVersion]);
    }

    setPickedRecordVersions(null);

    if (currentInsightForecastVersion) {
      setSelectedInsightForecastVersions([currentInsightForecastVersion]);
    }
    setResetKey((prev) => prev + 1);
  }, [
    forecastVersions,
    insightForecastVersions,
    setSelectedForecastVersions,
    setPickedRecordVersions,
    setSelectedInsightForecastVersions,
  ]);

  // Sources keep the order they were picked in, so adding one leaves the
  // chart's own production line where it was.
  const handleSourcesChange = useCallback(
    (sources: ProductionDataSource[]) =>
      setPickedSources({
        uwi,
        sources: [
          ...productionSources.filter((s) => sources.includes(s)),
          ...sources.filter((s) => !productionSources.includes(s)),
        ],
      }),
    [uwi, productionSources]
  );

  // Each source has its own versions, so a source that is no longer shown,
  // unpicked or from a change of well, loses its picked versions. Shown again,
  // it starts on its current version.
  const shownSourcesRef = useRef(productionSources);
  useEffect(() => {
    const dropped = shownSourcesRef.current.filter(
      (s) => !productionSources.includes(s)
    );
    shownSourcesRef.current = productionSources;
    if (dropped.length === 0) return;
    setPickedRecordVersions(
      (current) =>
        current && { ...current, versions: omit(current.versions, dropped) }
    );
  }, [productionSources]);

  const versionSelectors = (
    <div className={style.versionSelectorContainer}>
      <ProductionSourceMultiSelect
        value={productionSources}
        availableSources={availableSources}
        onChange={handleSourcesChange}
      />

      <VersionDropdown
        key={`record-${resetKey}`}
        versions={combinedRecordVersions}
        selectedVersions={combinedSelectedVersions}
        setSelectedVersions={(selected) =>
          setPickedRecordVersions((current) => ({
            uwi,
            versions: {
              ...(current?.uwi === uwi ? current.versions : {}),
              ...splitSourceVersions(
                selected,
                productionSources,
                recordVersionsBySource
              ),
            },
          }))
        }
        title="Production Versions"
        isLoading={isRecordVersionsLoading}
        isError={isAnyRecordVersionsError}
        onRefetch={refetchRecordVersions}
      />

      <VersionDropdown
        key={`forecast-legacy-${resetKey}`}
        versions={forecastVersions}
        selectedVersions={selectedForecastVersions}
        setSelectedVersions={setSelectedForecastVersions}
        title="Legacy PDP Forecast Versions"
        isLoading={isForecastVersionsLoading}
        isError={isForecastVersionsError}
        onRefetch={() => {
          setForecastVersionResponse(() => ({
            uwis: [uwi],
          }));
        }}
      />

      <DcaScenarioFilter value={dcaScenario} onChange={setDcaScenario} />

      <VersionDropdown
        key={`forecast-insights-${resetKey}`}
        versions={insightForecastVersions}
        selectedVersions={selectedInsightForecastVersions}
        setSelectedVersions={setSelectedInsightForecastVersions}
        title="Insights PDP Forecast Versions"
        isLoading={isInsightForecastVersionsLoading}
        isError={isInsightForecastVersionsError}
        onRefetch={() => {
          setInsightForecastVersionResponse(() => ({
            query: {
              wells: [{ uwi, dcaScenarioId: dcaScenario?.id }],
            },
          }));
        }}
      />

      {isForecastVersionsError && isRecordVersionsError && !isLoading ? (
        <Button
          kind="danger"
          renderIcon={ResetIcon}
          onClick={() => {
            refetchRecordVersions();
            setForecastVersionResponse(() => ({
              uwis: [uwi],
            }));
          }}
        >
          Refetch Versions
        </Button>
      ) : (
        <div>
          <Button
            disabled={
              isRecordVersionsLoading ||
              isForecastVersionsLoading ||
              isInsightForecastVersionsLoading
            }
            kind="tertiary"
            onClick={handleReset}
            className={style.resetButton}
          >
            Reset to Current
            <ResetIcon className={style.icon} />
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Grid condensed fullWidth>
      <Row>
        <Column>{versionSelectors}</Column>
      </Row>
      <Row>
        <Column>
          <WellAnalysisContent
            uwi={uwi}
            wellName={wellName}
            recordHref={recordHref}
            dailyRecordHref={dailyRecordHref}
            productionSources={productionSources}
            selectedRecordVersions={selectedRecordVersions}
            selectedForecastVersions={selectedForecastVersions}
            selectedInsightForecastVersions={selectedInsightForecastVersions}
            versionSelectors={versionSelectors}
            dcaScenario={dcaScenario}
          />
        </Column>
      </Row>
    </Grid>
  );
}

export const WellAnalysis = memo(WellAnalysisImpl);
